import { push } from 'react-router-redux';
import { delay } from 'redux-saga';
import { put, takeLatest, select, call } from 'redux-saga/effects';
import { searchElementMatch, setNotification, locales } from 'utils';
import * as SegmentActions from '../actions/segmentActions';
import * as SegmentApi from '../api/segmentApi';
import * as SegmentTypes from '../types/segmentTypes';

const getSegment = state => state.segment;
const getLayout = state => state.layout;

// L O A D   S E G M E N T S
function* loadSegmentsSaga() {
    try {
        const segment = yield select(getSegment);
        const layout = yield select(getLayout);

        const { offset, limit, sort } = segment;
        const { locale } = layout;

        const params = {
            filter: JSON.stringify({
                type: 'flat',
            }),
            offset,
            limit,
        };

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'name') {
                params.sort_by = JSON.stringify({ title: sortInt });
            } else if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        if (segment.filters.name !== '') {
            params.filter = JSON.stringify({
                title: searchElementMatch(segment.filters.name, locale),
                type: 'flat',
            });
        }

        const result = yield SegmentApi.getSegmentsApi(params);
        yield put(SegmentActions.loadSegmentsRes(result.data));
    } catch (e) {
        yield put(SegmentActions.loadSegmentsErr());
        console.log(e);
    }
}

export function* loadSegmentsWatcher() {
    yield takeLatest(SegmentTypes.LOAD_SEGMENTS, loadSegmentsSaga);
}
// L O A D   S E G M E N T S

// L O A D   E D I T A B L E   S E G M E N T S
function* loadEditableSegmentSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const query = { id: action.id };

        const result = yield SegmentApi.getSegmentsApi({ filter: JSON.stringify(query) });

        if (result.length === 0) {
            yield put(push('/404'));
            yield put(SegmentActions.loadEditableSegmentErr());
            setNotification('error', locale, 'global.error', 'segment.notification.editingSegmentError');
        } else {
            const [segment] = result.data.hits;

            if (segment) {
                yield put(SegmentActions.loadEditableSegmentRes(segment));
            } else {
                yield put(push('/404'));
                yield put(SegmentActions.loadEditableSegmentErr());
            }
        }
    } catch (e) {
        yield put(SegmentActions.loadEditableSegmentErr());
        setNotification('error', locale, 'global.error', 'segment.notification.editingSegmentError');
        console.log(e);
    }
}

export function* loadEditableSegmentWatcher() {
    yield takeLatest(SegmentTypes.LOAD_EDITABLE_SEGMENT, loadEditableSegmentSaga);
}
// L O A D   E D I T A B L E   S E G M E N T S

// F I L T E R   S E G M E N T S
function* setSegmentFiltersSaga(action) {
    try {
        const segment = yield select(getSegment);
        const layout = yield select(getLayout);

        const { offset, limit } = segment;
        const { locale } = layout;
        const params = {
            offset,
            limit,
            filter: JSON.stringify({
                title: searchElementMatch(action.value, locale),
                type: 'flat',
            }),
        };

        if (segment.sort !== null) {
            const sortInt = segment.sort.type === 'asc' ? 1 : -1;

            if (segment.sort.key === 'name') {
                params.sort_by = JSON.stringify({ title: sortInt });
            } else if (segment.sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        const result = yield SegmentApi.getSegmentsApi(params);
        yield put(SegmentActions.setSegmentsFiltersRes(result.data));
        yield call(delay, 500);
    } catch (e) {
        yield put(SegmentActions.loadSegmentsErr());
        console.log(e);
    }
}

export function* setSegmentsFilterWatcher() {
    yield takeLatest(SegmentTypes.SET_SEGMENTS_FILTER, setSegmentFiltersSaga);
}
// F I L T E R   S E G M E N T S

// R E S E T   F I L T E R   S E G M E N T S
function* resetFiltersSegmentSaga() {
    try {
        const segment = yield select(getSegment);
        const { offset, limit } = segment;

        const params = {
            offset,
            limit,
        };

        const result = yield SegmentApi.getSegmentsApi(params);
        yield put(SegmentActions.resetFiltersSegmentsRes(result.data));
    } catch (e) {
        yield put(SegmentActions.loadSegmentsErr());
    }
}

export function* resetFiltersSegmentsWatcher() {
    yield takeLatest(SegmentTypes.RESET_FILTER_SEGMENTS, resetFiltersSegmentSaga);
}
// R E S E T   F I L T E R   S E G M E N T S

// S E T   D I S A B L E   S E G M E N T
function recursiveSegment(data, id, value) {
    return data.map((hit) => {
        const segment = hit;

        if (id === segment.id) {
            segment.status = value;
        }

        if (segment.children) {
            return {
                ...segment,
                children: recursiveSegment(segment.children, id, value),
            };
        }

        return segment;
    });
}

function* toggleSegmentStatusSaga(action) {
    const segment = yield select(getSegment);
    const layout = yield select(getLayout);
    const { locale } = layout;
    let segmentsList = segment.listing ? segment.listing.hits : '';

    try {
        const params = {
            segment_id: action.key,
            status: action.value,
        };

        const result = yield SegmentApi.enableDisableStatusSegmentApi(params);
        if (result.status === 202) {
            segmentsList = recursiveSegment(segmentsList, action.key, action.value);
            yield put(SegmentActions.loadSegmentsRes({ ...segment.listing, hits: segmentsList }));
        } else {
            setNotification('error', locale, 'global.error', 'segment.notification.disableSegmentError');
            yield put(SegmentActions.loadSegmentsErr());
        }
    } catch (e) {
        yield put(SegmentActions.loadSegmentsErr());
        console.log(e);
    }
}

export function* toggleSegmentStatusWatcher() {
    yield takeLatest(SegmentTypes.TOGGLE_SEGMENT_STATUS, toggleSegmentStatusSaga);
}
// S E T   D I S A B L E   S E G M E N T

// L O A D   S U B   S E G M E N T S
function* loadSubSegmentsSaga(action) {
    try {
        const { listing } = yield select(getSegment);

        const params = {
            offset: 0,
            limit: 99,
            filter: JSON.stringify({
                parent: action.data.key,
                type: 'flat',
            }),
        };

        const result = yield SegmentApi.getSegmentsApi(params);
        yield put(SegmentActions.loadSubSegmentsRes(result.data));

        const recursiveMap = (hits) => {
            if (hits) {
                return hits.map((hit) => {
                    const child = hit;

                    if (child.id === action.data.key) {
                        child.children = result.data.hits;
                        child.descendents = 1;
                    } else {
                        child.children = recursiveMap(child.children);
                    }

                    return child;
                });
            }

            return [];
        };

        yield put(SegmentActions.loadSegmentsRes({ total: listing.total, hits: recursiveMap(listing.hits) }));
    } catch (e) {
        yield put(SegmentActions.loadSubSegmentsErr());
        console.log(e);
    }
}

// eslint-disable-next-line import/prefer-default-export
export function* loadSubSegmentsWatcher() {
    yield takeLatest(SegmentTypes.LOAD_SUB_SEGMENTS, loadSubSegmentsSaga);
}
// L O A D   S U B   S E G M E N T S


// A D D   N E W   S E G M E N T
function* updateSegmentTranslateSaga(action) {
    const { locale } = yield select(getLayout);

    const data = {
        segment_id: action.data.segment_id,
        reseller_id: action.data.reseller_id,
        slug: '',
    };

    const params = { bulk: [] };
    locales.forEach((lc) => {
        if (lc !== 'en' || (lc === 'en' && action.type === SegmentTypes.EDIT_SEGMENT)) {
            params.bulk.push({
                ...action.data[lc],
                ...data,
            });
        }
    });

    const result = yield SegmentApi.updateSegmentTranslateApi(params);

    if (result.status !== 202) {
        const error = yield result.json();
        setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
    }
}

function* addNewSegmentSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const finalDescription = 'segment.notification.newSegmentSuccess';

    try {
        const data = {
            ...action.data,
            ...action.data.en,
        };
        const result = yield SegmentApi.addNewSegmentApi(data);

        if (result.status === 202) {
            yield put(SegmentActions.addNewSegmentRes(true, false));
            yield call(updateSegmentTranslateSaga, action);

            yield delay(1000);
            yield put(SegmentActions.addNewSegmentRes(false, false));

            if (action.data.isPopup) {
                yield put({ type: SegmentTypes.LOAD_SUB_SEGMENTS, data: { key: action.data.parent } });
                // yield call(loadSegmentsSaga, {});
            } else {
                yield put(push('/marketing/segments/'));
            }

            setNotification('success', locale, 'global.success', finalDescription);
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(SegmentActions.addNewSegmentErr(error.message));
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'segment.notification.newSegmentError');
        yield put(SegmentActions.addNewSegmentErr());
        console.log(e);
    }
}

export function* addSegmentWatcher() {
    yield takeLatest(SegmentTypes.ADD_NEW_SEGMENT, addNewSegmentSaga);
}
// A D D   N E W   S E G M E N T

// E D I T   S E G M E N T
function* editSegmentSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    try {
        const data = {
            segment_id: action.data.segment_id,
            reseller_id: action.data.reseller_id,
            parent_segment_id: action.data.parent_segment_id,
            type: action.data.type,
            status: action.data.status,
            tags: action.data.tags,
            slug: '',
            ...action.data.en,
        };
        const result = yield SegmentApi.editSegmentApi(data);

        if (result.status === 202) {
            setNotification('success', locale, 'global.success', 'segment.notification.editSegmentSuccess');
            yield call(updateSegmentTranslateSaga, action);
        }

        yield put(push('/marketing/segments/'));
        yield put(SegmentActions.editSegmentRes(true, true));
        yield delay(1000);
        yield put(SegmentActions.editSegmentRes(false, false));
    } catch (e) {
        yield put(SegmentActions.editSegmentErr());
        setNotification('error', locale, 'global.error', 'segment.notification.editSegmentError');
        console.log(e);
    }
}

export function* editSegmentWatcher() {
    yield takeLatest(SegmentTypes.EDIT_SEGMENT, editSegmentSaga);
}
// E D I T   S E G M E N T
